import { useEffect, useState } from 'react';
import MultipleSelectCheckmarks from '../../../../components/ui-control/multiSelect';
import { getDefendantId, getDefendantName } from '../../../../utils/helper';
import { Checkbox } from '@mui/material';
import { getSafeImageUrl } from '../../../../utils/securityUtil';
import FileUploadIcon from '../../../../assets/icons/upload_file.svg?react';
import UploadIcon from '../../../../assets/icons/icon_upload.svg?react';
import LargeIcon from '../../../../assets/icons/open_in_full.svg?react'
import { useDownloadOptions } from '../../../../hooks/useDownloadOptions';
import FileButtonOptions from '../../../../components/common/fileButtonOption';
import XButton from '../../../../components/common/XButton';
import XModal from '../../../../components/common/XModal';
import InfoText from '../../../../components/ui-control/InfoText';
import FileUploadErrorModal from '../../../../utils/fileUploadErrorModal';
import UploadButtonGroup from '../../../../components/common/UploadButtonGroup';
import useDropboxUploadHandler from '../../../../hooks/useDropboxUploadHandler';
import { reportSectionClasses } from '../../../../components/common/formClasses';
import { getPreviewUrlForFile, getPreviewUrlForStoredImage } from '../../../../utils/imagePreview';

const imageGridClasses =
    "grid [grid-template-columns:repeat(auto-fill,minmax(calc(var(--spacing)*55),1fr))] gap-2.5";

// The Arches drop zone is a Stone block with the dashed Sand rule inset inside it.
const dropZonePanelClasses = "rounded-lg bg-surface-alt p-6";

const dropZoneClasses =
    "border-2 border-dashed border-border-accent rounded-lg p-10 " +
    "text-center text-disabled transition-colors duration-300 ease-in-out";

const imageCardClasses =
    "group rounded-lg p-2.5 bg-white text-center relative " +
    "transition-[transform,box-shadow] duration-300 ease-in-out " +
    "shadow-signature-card z-[1] " +
    "hover:scale-105 hover:shadow-[0_4px_15px_rgba(0,0,0,0.2)] " +
    "relative mb-2";

const hoverActionIconsClasses =
    "hidden group-hover:[display:unset] absolute right-4 top-5 " +
    "[&_button]:w-10 [&_button]:h-10 [&_button]:shadow-signature-action";

const hoverExpandIconClasses =
    "hidden group-hover:[display:unset] absolute left-4 top-5 " +
    "[&_button]:w-10 [&_button]:h-10 [&_button]:shadow-signature-action";

const imageInfoClasses =
    "inline-flex items-start self-stretch flex-col w-full " +
    "justify-start gap-2 mt-2 " +
    "[&_input[type=checkbox]]:mr-[5px] " +
    "[&_label]:w-[70%] [&_label]:overflow-hidden [&_label]:whitespace-nowrap " +
    "[&_label]:text-ellipsis [&_label]:inline-block";

const XMultiImageUpload = ({
    heading,
    subHeading,
    label,
    acceptedFileTypes = ".jpg,.jpeg,.png,.heic,.heif",
    initialFiles = [],
    onChange,
    defendants=null,
    formik,
    selectedDefendant,
    isLoading,
    onIsSelectedChange,
    onRemove,
    onDefendantsChange
}) => {
    const [filesData, setFilesData] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const {downloadSingleFile} = useDownloadOptions()
   
    useEffect(() => {
        let isMounted = true;
        const loadInitialFiles = async () => {
            if (initialFiles?.length > 0) {
                const initialFileObjects = await Promise.all(initialFiles.map(async (v) => ({
                    id: Math.random().toString(36).substr(2, 9),
                    url: await getPreviewUrlForStoredImage({ url: v?.url, fileName: v?.name }),
                    name: v?.name,
                    isSelected: !!v?.isSelected,
                    isLiability: !!v?.isLiability,
                    s3UrlPath: v?.s3UrlPath,
                    file: v?.file,
                    defendants : v?.defendants || []
                })));
                if (isMounted) {
                    setFilesData(initialFileObjects);
                }
            } else if (isMounted) {
                setFilesData([]);
            }
        };
        loadInitialFiles();
        return () => {
            isMounted = false;
        };
    }, [initialFiles]);

    const getApplicableDefendants =()=>{
        const allDefIds = (defendants || []).map(d => d.defendantId);
        const isSingleLiability = formik?.values?.caseInfo?.isSingleLiabilitySectionForAllDefendants ?? true;
        return isSingleLiability ? allDefIds : [];
    }

   

    const handleFiles = async (uploadContent) => {
        const newFiles = await Promise.all(Array.from(uploadContent.file).map(async (file) => ({
            id: Math.random().toString(36).substr(2, 9),
            file: file,
            url: await getPreviewUrlForFile(file),
            name: file.name,
            isSelected: false,
            isLiability: false,
            defendants: getApplicableDefendants()
        })));

        // Filter out duplicates based on filename
        const uniqueNewFiles = newFiles.filter(newFile =>
            !filesData.some(existingFile => existingFile.name === newFile.name)
        );

        const updatedFiles = [...filesData, ...uniqueNewFiles];
        
        setFilesData(updatedFiles);
        onChange(updatedFiles);
    };

    const {
        handleDropboxSuccess,
        fileErrorList,
        setFileErrorList
    } = useDropboxUploadHandler({
        accept: acceptedFileTypes,
        onFilesReady: async (validFiles) => {
            handleFiles({ file: validFiles });
        }
    });

    const getSelectionDataArrayFromDefendantsArray = (defendants)=>{
        return defendants.map(def=>{
            return{
                id: getDefendantId(def),
                name: getDefendantName(def)
            }
        })
    }


    const handleSelectFile = (id) => {
        const file = filesData.find(f => f.id === id);
        const newIsSelected = !file.isSelected;

        const updatedFiles = filesData.map(fileData =>
            fileData.id === id
                ? { ...fileData, isSelected: newIsSelected }
                : fileData
        );
        setFilesData(updatedFiles);
        onChange(updatedFiles);

        if (onIsSelectedChange && file?.s3UrlPath) {
            onIsSelectedChange(file.s3UrlPath, newIsSelected);
        }
    };

    const removeFile = (id, fileData) => {
        const updatedFiles = filesData.filter(fileData => fileData.id !== id);
        setFilesData(updatedFiles);
        onChange(updatedFiles);

        if (onRemove && fileData?.s3UrlPath) {
            onRemove(fileData.s3UrlPath);
        }
    };


    const nextImage = () => {
        setCurrentIndex((currentIndex + 1) % filesData.length);
    };

    const previousImage = () => {
        setCurrentIndex((currentIndex - 1 + filesData.length) % filesData.length);
    };


    const openModal = (i) => {
        setCurrentIndex((currentIndex + i) % filesData.length);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setCurrentIndex(0);
        setIsModalOpen(false);
    };


    const handleIsLiabilityAddedCheckbox = (e, index) => {
        const file = filesData[index];
        const newIsLiability = e.target.checked;

        const updatedArr = filesData.map((item, id) =>
            id === index ? { ...item, isLiability: newIsLiability } : item
        );

        setFilesData(updatedArr);
        onChange(updatedArr);

        if (onIsSelectedChange && file?.s3UrlPath) {
            onIsSelectedChange(file.s3UrlPath, newIsLiability);
        }
    }

    function truncateFilename(filename, maxLength = 22) {
        if (!filename) return "";
        const dotIndex = filename.lastIndexOf(".");
        if (dotIndex === -1) return filename;
        
        if (filename.length <= maxLength) return filename;
        const name = filename.slice(0, dotIndex);
        const ext = filename.slice(dotIndex);
        const dots = "...";
        const keep = Math.max(0, maxLength - ext.length - dots.length);
        return name.slice(0, keep) + dots + ext;
    }


    return (
        <>
            <XModal
                isOpen={isModalOpen}
                title={filesData[currentIndex]?.name}
                subtitle={filesData.length > 0 ? `${currentIndex + 1} of ${filesData.length} ${label}` : ''}
                onClose={closeModal}
                size="lg"
            >
                <img src={getSafeImageUrl(filesData[currentIndex]?.url, "/placeholder.png")} alt={`${currentIndex + 1}`} className="img-fluid mx-auto! block!" />
                <div className="flex items-center justify-between mt-4">
                    <XButton variant="link" onClick={previousImage}>Previous</XButton>
                    <XButton variant="link" onClick={nextImage}>Next</XButton>
                </div>
            </XModal>
            <div className="form-group mb-0!">
                <div className='mb-4'>
                    {heading && <h2 className='mb-0!'>Accident Scene and Property Damage Photos</h2>}
                    {subHeading && <label>Bodily Injury Photos</label>}
                    {filesData.length > 30 && <InfoText message="Uploading many images may cause issues opening and sending the final document."/>}
                </div>
                <div className={`${reportSectionClasses} p-6`}>
                    {filesData.length > 0 && (
                        <div className="row g-3 mb-4">
                            <div className={`col-md-12 col-sm-4 ${imageGridClasses}`}>
                                {filesData.map((fileData, index) => {
                                     const { name, s3UrlPath } = fileData
                                    const fileInfo = { name, s3UrlPath };
                                    return (
                                        <div className={imageCardClasses} key={index}>
                                            <img key={fileData} src={getSafeImageUrl(fileData.url, "/placeholder.png")} alt={fileData?.name} className="w-full h-50 object-cover" />
                                            <FileButtonOptions
                                                divClassName={hoverActionIconsClasses}
                                                onDownload={() => downloadSingleFile(fileInfo)}
                                                onDelete={() => removeFile(fileData.id, fileData)}
                                                isLoading={isLoading}
                                            />
                                            {!isLoading &&<div className={hoverExpandIconClasses}>
                                                <XButton
                                                    variant="close"
                                                    onClick={() => openModal(index)}
                                                >
                                                    <LargeIcon />
                                                </XButton>
                                            </div>}
                                            <div className={imageInfoClasses}>
                                                {defendants ?
                                                    <MultipleSelectCheckmarks dataArr={getSelectionDataArrayFromDefendantsArray(defendants)}
                                                        initValue={fileData.defendants}
                                                        imageName={truncateFilename(fileData.name)}
                                                        isLiability={fileData.isLiability}
                                                        setFilesData={setFilesData}
                                                        index={index}
                                                        handleSelect={(selectedData) => {
                                                            const updatedDefendants = selectedData.map(elem => elem.id);
                                                            const updatedFiles = filesData.map(elem =>
                                                                elem.id === fileData.id
                                                                    ? {...elem, defendants: updatedDefendants}
                                                                    : elem
                                                            );
                                                            setFilesData(updatedFiles);
                                                            onChange(updatedFiles);

                                                            if (onDefendantsChange && fileData.s3UrlPath) {
                                                                onDefendantsChange(fileData.s3UrlPath, updatedDefendants);
                                                            }
                                                        }}
                                                        formik={formik}
                                                        selectedDefendant={selectedDefendant}
                                                        handleIsLiabilityAddedCheckbox={handleIsLiabilityAddedCheckbox}
                                                        disabled={isLoading}
                                                    />
                                                    :
                                                    <div className="flex gap-1.5">
                                                        <Checkbox
                                                            checked={fileData.isSelected}
                                                            sx={{padding: 0}}
                                                            disabled={isLoading}
                                                            onChange={() => handleSelectFile(fileData.id)}
                                                        />
                                                        <span className="text-sm whitespace-nowrap">
                                                            {truncateFilename(fileData.name, 25)}
                                                        </span>
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )}
                    <div className={dropZonePanelClasses}>
                        <div className={dropZoneClasses}>
                            <div className="mb-6 flex justify-center text-[calc(var(--spacing)*16)] text-subtle">
                                <FileUploadIcon/>
                            </div>
                            <p className="text-base text-subtle mb-6">Drag and drop your files here or</p>
                            <UploadButtonGroup
                                accept={acceptedFileTypes}
                                multiple={true}
                                disabled={isLoading}
                                onFileSelect={handleFiles}
                                onDropboxSuccess={handleDropboxSuccess}
                                className="justify-center"
                                inputName="files"
                                renderUploadButton={() => (
                                    <XButton variant="outlinePrimary" className="mx-auto!">
                                        <UploadIcon className="text-primary" />
                                        Upload
                                    </XButton>
                                )}
                            />
                        </div>
                    </div>
                </div>
                {fileErrorList.length > 0 && (
                    <FileUploadErrorModal
                        fileErrorList={fileErrorList}
                        setFileErrorList={setFileErrorList}
                    />
                )}
            </div>
        </>
    );
};

export default XMultiImageUpload;